import type {
    AddressNew,
    AddressOld,
    AddressOldAddress,
    EmailNew,
    EmailOld,
    NewStructureContact,
    OldStructureContact,
    PhoneNew,
    PhoneOld,
} from './ContactAdapterTypes';
import { ContactFlag, ContactType } from './ContactDataManagementTypes';

const EMAIL = 'EMAIL';
const PHONE = 'PHONE';
const ADDRESS = 'ADDRESS';

export const isDefined = <T>(value?: T): value is T => !!value;

export const isNewAddress = (contact: NewStructureContact): contact is AddressNew =>
    contact.type === ContactType.ADDRESS;
export const isNewEmail = (contact: NewStructureContact): contact is EmailNew =>
    contact.type === ContactType.EMAIL;
export const isNewPhone = (contact: NewStructureContact): contact is PhoneNew =>
    contact.type === ContactType.PHONE;

const isOldAddress = (contact: OldStructureContact): contact is AddressOld =>
    contact.type === ADDRESS;
const isOldEmail = (contact: OldStructureContact): contact is EmailOld => contact.type === EMAIL;
const isOldPhone = (contact: OldStructureContact): contact is PhoneOld => contact.type === PHONE;

const getNewContactFlags = (flags?: string[]): ContactFlag[] => {
    if (flags?.includes(ContactFlag.MAIN_CONTACT)) {
        return [ContactFlag.MAIN_CONTACT];
    }

    return [];
};

function getROSpecificParts({
    streetNumber,
    buildingApartment,
    'ro-stair': stair,
    'ro-floor': floor,
    'ro-apartment': apartment,
}: Partial<AddressOldAddress>): (string | undefined)[] {
    return [
        streetNumber && `Nr. ${streetNumber}`,
        buildingApartment && `Bl. ${buildingApartment}`,
        stair && `Sc. ${stair}`,
        floor && `Et. ${floor}`,
        apartment && `Ap. ${apartment}`,
    ];
}

function isRoAddress(address: Partial<AddressOldAddress>): boolean {
    return (
        address.country === 'RO' ||
        Object.hasOwn(address, 'ro-stair') ||
        Object.hasOwn(address, 'ro-floor') ||
        Object.hasOwn(address, 'ro-apartment')
    );
}

const BUILDING_APARTAMENT_DELIMETER = '/';
function getStreetNumberAndOthers(address: Partial<AddressOldAddress>): string {
    if (isRoAddress(address)) {
        return getROSpecificParts(address).filter(isDefined).join(', ');
    }

    return [address.buildingApartment, address.streetNumber]
        .filter(isDefined)
        .join(BUILDING_APARTAMENT_DELIMETER);
}

const EMPTY_SPACE_DELIMETER = ' ';
const getNewAddressStreet = ({ street, ...rest }: AddressOldAddress): string | undefined => {
    const streetParts = [street, getStreetNumberAndOthers(rest)];

    if (streetParts.length === 0) {
        return undefined;
    }

    return streetParts.join(EMPTY_SPACE_DELIMETER);
};

function transformOldAddressToNew({ address, flags, id }: AddressOld): AddressNew {
    const newAddressFormat = {
        street: getNewAddressStreet(address),
        postBox: address.postBox,
        zipCode: address.zipCode,
        city: address.city,
        country: address.country,
    };
    return {
        id,
        type: ContactType.ADDRESS,
        flags: getNewContactFlags(flags),
        address: newAddressFormat,
    };
}

function transformOldEmailToNew({ email, id, flags }: EmailOld): EmailNew {
    return {
        id,
        type: ContactType.EMAIL,
        flags: getNewContactFlags(flags),
        email: email?.email ? { address: email.email } : undefined,
    };
}

function transformOldPhoneToNew({ phone, id, flags }: PhoneOld): PhoneNew {
    return {
        id,
        type: ContactType.PHONE,
        flags: [...getNewContactFlags(flags), ContactFlag.MOBILE_PHONE_NUMBER],
        phone: phone
            ? {
                  number: [phone.countryCallingCode, phone.areaCallingCode, phone.phoneNumber]
                      .filter(Boolean)
                      .join(''),
              }
            : undefined,
    };
}

export function transformOldContactToNew(
    contact: OldStructureContact,
): AddressNew | EmailNew | PhoneNew | undefined {
    if (isOldEmail(contact)) {
        return transformOldEmailToNew(contact);
    }
    if (isOldPhone(contact)) {
        return transformOldPhoneToNew(contact);
    }
    if (isOldAddress(contact)) {
        return transformOldAddressToNew(contact);
    }

    return undefined;
}
